"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";

import { analyticsConfig } from "@/lib/analytics/config";

/**
 * Meta Pixel (aka Facebook Pixel). Loads the fbevents.js snippet once,
 * tracks PageView on every client-side route change. Silently no-ops
 * when NEXT_PUBLIC_META_PIXEL_ID is not configured.
 *
 * The `Script` component with strategy="afterInteractive" ensures the
 * pixel doesn't block first paint.
 */
export function MetaPixel() {
  const pixelId = analyticsConfig.metaPixel.id;
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pixelId || typeof window === "undefined") return;
    const fbq = (window as MetaPixelWindow).fbq;
    if (typeof fbq !== "function") return;
    fbq("track", "PageView");
    // We include searchParams in the dep array so navigations that only
    // change query params still fire a PageView.
  }, [pixelId, pathname, searchParams]);

  if (!pixelId) return null;

  return (
    <>
      <Script id="meta-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        {/* Fallback pageview pixel for no-JS visitors */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=""
          height="1"
          width="1"
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}

interface MetaPixelWindow extends Window {
  fbq?: (...args: unknown[]) => void;
}
